import {
  ContractSystemType,
  IngestionPlatform,
  UsState,
} from '@client/graphql/__generated__/types';
import isNil from 'lodash/isNil';
import { useFeatureFlagPayload } from 'posthog-js/react';

export enum FeatureKey {
  MAKE_OFFER = 'make-offer',
  OPEN_HOUSES = 'open-houses',
  OFFER_COACH = 'offer-coach',
  ORG_PIPELINE = 'org-pipeline',
  SYNC_KEY_TERMS_TO_DARWIN = 'sync-key-terms-to-darwin',
  KEY_TERMS_PDF = 'key-terms-pdf',
  DOTLOOP_COMPLIANCE_GROUP = 'dotloop-compliance-group',
  ESSENTIALS_PRODUCT = 'essentials-product',
  DATA_COACH_CHAT = 'data-coach-chat',
  ORG_COMPLIANCE_DASHBOARD = 'org-compliance-dashboard',
  BROKERSUMO_TRANSACTION_DATA = 'brokersumo-transaction-data',
  EXTENSION_REVIEW_QUEUES = 'extension-review-queues',
}

interface UseIsFeatureEnabledForPayloadProps {
  featureFlagKey: FeatureKey;
  mlsOSN?: string;
  state?: UsState;
  organizationId?: string;
  userId?: string;
  platform?: ContractSystemType | IngestionPlatform;
}

/**
 * Example payload from PostHog feature flag:
 * {
 *   state: [UsState.CA, UsState.TX],    // US states where the feature is enabled
 *   mls: ['CanopyMLS', 'TestMLS'],      // MLS originating system names
 *   // Ingestion and contract-system platforms
 *   platforms: ['SKYSLOPE', 'DOTLOOP', 'TRANSACTION_DESK'],
 * }
 */

interface FeatureFlagPayload {
  state?: UsState[];
  mls?: string[];
  organizations?: string[];
  users?: string[];
  platforms?: (ContractSystemType | IngestionPlatform)[];
  enabledForEveryone?: boolean;
}

export const useGetFeatureFlagPayload = (
  featureFlagKey: FeatureKey | undefined
) => {
  return useFeatureFlagPayload(featureFlagKey ?? '') as
    | FeatureFlagPayload
    | undefined;
};

export const useIsFeatureEnabledForPayload = ({
  featureFlagKey,
  ...rest
}: UseIsFeatureEnabledForPayloadProps) => {
  const payload = useGetFeatureFlagPayload(featureFlagKey);

  // make all features available in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (!payload) {
    // ! TODO: Handle this with feature flag bootstrapping:
    // ! https://posthog.com/tutorials/bootstrap-feature-flags-react
    // payload is still loading
    return undefined;
  }

  if (payload?.enabledForEveryone) {
    return true;
  }

  if (Object.values(rest).every((value) => isNil(value))) {
    return false;
  }

  const { state, mlsOSN, organizationId, userId, platform } = rest;

  if (state && payload?.state?.includes(state)) {
    return true;
  }

  if (platform && payload?.platforms?.includes(platform)) {
    return true;
  }

  if (mlsOSN && payload?.mls?.includes(mlsOSN)) {
    return true;
  }

  if (organizationId && payload?.organizations?.includes(organizationId)) {
    return true;
  }

  if (userId && payload?.users?.includes(userId)) {
    return true;
  }

  return false;
};
